// Native Android call-log bridge.
// `window.Capacitor` is injected by @capacitor/core even in the browser build, so the
// correct native check is `isNativePlatform()` — otherwise registerPlugin proxies throw
// "plugin is not implemented on web" when any method is invoked.
const isNative = !!(window as any).Capacitor?.isNativePlatform?.();

export const isCallLogAvailable = isNative;

// Mirrors android.provider.CallLog.Calls
export const CALL_TYPE = {
  INCOMING: 1,
  OUTGOING: 2,
  MISSED: 3,
  VOICEMAIL: 4,
  REJECTED: 5,
  BLOCKED: 6,
} as const;

export interface DeviceCall {
  number: string | null;
  cachedName: string | null;
  /** 1 incoming, 2 outgoing, 3 missed, 4 voicemail, 5 rejected, 6 blocked */
  type: number;
  /** epoch millis */
  date: number;
  /** seconds */
  duration: number;
}

/** Last native failure, kept so the on-screen diagnostics panel can show the
 *  real reject message. Developer options is blocked on some vendor ROMs
 *  (vivo/Funtouch in particular), which rules out logcat — this is the
 *  substitute for it. */
let lastError: string | null = null;
export const getLastCallLogError = () => lastError;

function recordError(e: any): string {
  const msg: string = e?.message || e?.errorMessage || String(e) || 'Unknown error';
  lastError = msg;
  return msg;
}

// Boxed rather than held bare: registerPlugin returns a Proxy that turns *any*
// property access into a native call, so returning it straight out of an async
// function makes the promise machinery probe `.then` on it and the proxy
// forwards that to native — "CallLog.then() is not implemented on android".
// Wrapping it keeps the proxy off the await path.
interface PluginBox { p: any }

let pluginPromise: Promise<PluginBox | null> | null = null;

/**
 * Hands back the BOX, never the bare proxy — callers read `.p` themselves.
 *
 * Returning the proxy from an async function (or resolving a promise with it)
 * is enough to break it: Promise.resolve probes `.then` on the value, the proxy
 * turns that property access into a native call, and it blows up with
 * "CallLog.then() is not implemented on android". Keeping it boxed means the
 * proxy never sits on an await path.
 */
function getPluginBox(): Promise<PluginBox | null> {
  if (!isNative) return Promise.resolve(null);
  if (!pluginPromise) {
    pluginPromise = (async () => {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        return { p: registerPlugin('CallLog') };
      } catch (e) {
        recordError(e);
        return null;
      }
    })();
  }
  return pluginPromise;
}

/**
 * registerPlugin always hands back a proxy, even when the native class is
 * missing from the installed APK — the failure only surfaces when a method is
 * called. So "is the plugin really there?" means actually invoking something.
 */
export async function isPluginInstalled(): Promise<boolean> {
  const box = await getPluginBox();
  if (!box) return false;
  try {
    await box.p.checkPermission();
    return true;
  } catch (e) {
    recordError(e);
    return false;
  }
}

export async function checkCallLogPermission(): Promise<boolean> {
  const box = await getPluginBox();
  if (!box) return false;
  try {
    const res = await box.p.checkPermission();
    return !!res?.granted;
  } catch (e) {
    recordError(e);
    return false;
  }
}

export async function requestCallLogPermission(): Promise<boolean> {
  const box = await getPluginBox();
  if (!box) return false;
  try {
    const res = await box.p.requestPermission();
    return !!res?.granted;
  } catch (e) {
    recordError(e);
    return false;
  }
}

/**
 * @param sinceMillis only calls newer than this (0 = no lower bound). Bounding
 *   the query in the content provider is far cheaper than reading every row and
 *   discarding most of them — handsets routinely hold thousands of calls.
 */
export async function getDeviceCallLog(limit = 200, sinceMillis = 0): Promise<DeviceCall[]> {
  const box = await getPluginBox();
  if (!box) return [];
  try {
    const res = await box.p.getCallLog({ limit, sinceMillis });
    return (res?.calls || []) as DeviceCall[];
  } catch (e) {
    recordError(e);
    throw e;
  }
}

export interface CallLogDiagnostics {
  /** Running inside the Capacitor APK at all (false in a desktop browser). */
  isNative: boolean;
  /** The CallLog native class is present in the installed APK. */
  pluginInstalled: boolean;
  permission: 'granted' | 'denied' | 'unavailable';
  /** Calls the provider returned, or null if it was never reached. */
  rawCallCount: number | null;
  lastError: string | null;
  platform: string;
  userAgent: string;
}

/**
 * Everything needed to work out why the screen is empty, gathered in one pass
 * so it can be read off the handset (or copied and sent) instead of logcat.
 */
export async function probeCallLog(): Promise<CallLogDiagnostics> {
  const diag: CallLogDiagnostics = {
    isNative,
    pluginInstalled: false,
    permission: 'unavailable',
    rawCallCount: null,
    lastError: null,
    platform: (window as any).Capacitor?.getPlatform?.() || 'web',
    userAgent: navigator.userAgent,
  };

  if (!isNative) {
    diag.lastError = 'Not running inside the Android app.';
    return diag;
  }

  diag.pluginInstalled = await isPluginInstalled();
  if (!diag.pluginInstalled) {
    diag.lastError = lastError || 'CallLog plugin not found in the installed APK.';
    return diag;
  }

  diag.permission = (await checkCallLogPermission()) ? 'granted' : 'denied';
  if (diag.permission === 'granted') {
    try {
      const rows = await getDeviceCallLog(300);
      diag.rawCallCount = rows.length;
    } catch {
      diag.rawCallCount = null;
    }
  }

  diag.lastError = lastError;
  return diag;
}

/**
 * Reduce a dialled/stored number to the digits that actually identify the
 * subscriber, so `+91 98765-43210`, `098765 43210` and `9876543210` all match.
 * Indian mobile numbers are 10 digits; shorter strings (landline extensions,
 * service codes) are kept whole rather than truncated.
 */
export function normalizeNumber(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
}

/**
 * The number as it should be shown and stored: +91, a leading 0 and any spaces
 * stripped off.
 *
 * The handset reports the same subscriber inconsistently — "+919876543210",
 * "09876543210" and "9876543210" are all the same person — which made one
 * contact look like several, and the raw form overflowed the 10-character
 * mobile_no column when saved. Anything that does not reduce to a clean
 * 10-digit number (a short code, an international caller) is shown untouched
 * rather than silently truncated to something wrong.
 */
export function displayNumber(raw: string | null | undefined): string {
  if (!raw) return '';
  const n = normalizeNumber(raw);
  return n.length === 10 ? n : String(raw).trim();
}
